/**
 * بدنه‌ی مشترکِ هر دو صفحه‌ی بی‌حساب — قرینه‌ی
 * apps/mobile/src/features/signals/GuestSignal.tsx.
 *
 *   /s/:token   PublicSignalPage   یکی برای همه، جواب نمی‌گیرد.
 *   /i/:token   InvitePage         مالِ یک نفرِ مشخص، جواب می‌گیرد.
 *
 * ═══ چرا این فایل ساخته شد ═══
 *
 * لینکِ پیامک که توی مرورگر باز می‌شد، فقط یک عنوان بود و سه دکمه. همان
 * سیگنال داخلِ اپ یک صفحه‌ی کامل است: نوعش، هزینه‌اش، وضعیتِ لغو، و
 * سربرگِ گفتگو. آدمی که با لینک می‌آید دقیقاً همان کسی‌ست که هنوز قانع
 * نشده — دیدنِ نسخه‌ی لخت بدترین معرفیِ ممکن است.
 *
 * ═══ ترتیب: اول «به چی دعوتم»، آخر «می‌آیی؟» ═══
 *
 * اولین نسخه‌ی این صفحه از روی `SignalPage` کپی شده بود و همان ترتیب را
 * داشت: جوابِ خودت بالا، مشخصات پایین. برای کاربرِ حساب‌دار درست است — او
 * برنامه را از قبل می‌شناسد و فقط آمده جواب بدهد. برای کسی که با یک پیامک
 * از بیرون می‌آید برعکس است: سه دکمه‌ی «هستم / شاید / نمی‌تونم» اولین چیزی
 * بود که می‌دید، و زمان و مکان و هزینه زیرش، ریز و بی‌برچسب.
 *
 * پس ترتیب حالا همان ترتیبِ سوالِ آدم است: این از طرفِ کیست، چیست، کِی و
 * کجا و چند، حرفِ میزبان — و **بعد** «می‌آیی؟».
 *
 * ═══ بعد از جواب، یک صفحه‌ی دیگر ═══
 *
 * تا وقتی طرف جواب نداده، این صفحه فقط یک کار دارد و پیشنهادِ نصبِ اپ
 * حواس‌پرتی‌ست — پس ته صفحه فقط یک لینکِ ریز است. لحظه‌ای که می‌گوید
 * «هستم» یا «شاید»، همان پیشنهاد دیگر تبلیغ نیست: او واقعاً می‌آید و
 * واقعاً باید بداند اگر ساعت عوض شد چه می‌شود. آن‌جا کارتِ کامل می‌آید.
 *
 * `done` عمداً فقط در همین نشست زندگی می‌کند: با رفرش دوباره کلِ صفحه را
 * می‌بیند، چون شاید برای خواندنِ دوباره‌ی برنامه برگشته باشد.
 *
 * ═══ چهار چیزی که عمداً کم است ═══
 *
 *   • «کی هست». مهمان نمی‌بیند چه کسِ دیگری دعوت شده — همان قانونِ FOMO،
 *     این‌بار از سمتِ بیرون. دیتابیس هم اصلاً به این دو دریچه نمی‌دهدش.
 *   • گفتگو. قبلاً دو سربرگِ «جزئیات / گفتگو» این‌جا بود، کپیِ صفحه‌ی داخلِ
 *     اپ؛ ولی سربرگِ گفتگو از دریچه‌ی مهمان هیچ‌وقت چیزی نداشت جز یک جمله‌ی
 *     «اپ را نصب کن». یعنی یکی از دو سربرگِ صفحه بن‌بست بود — و سربرگ وقتی
 *     فقط یکی‌اش کار می‌کند، اصلاً سربرگ نیست. هر دو برداشته شدند.
 *   • اسمِ خودِ دعوت‌شده. لینک از پیامک/ایمیلِ خودش آمده؛ «سلام سارا» چیزی
 *     به او نمی‌گوید که نداند، و اگر لینک دستِ کسِ دیگری بیفتد فقط اسمِ او
 *     را لو می‌دهد.
 *   • ویرایش، تاریخچه‌ی نسخه‌ها، قطعی کردن. کارِ سازنده‌اند و سازنده
 *     حساب دارد.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { guestView, type GuestSignalSource } from '@/core/domain/guestView';
import { kindMeta } from '@/core/domain/signalKinds';
import type { GuestResponse } from '@/core/data/inviteRepo';
import { t, useLocale } from '@/core/lib/i18n';
import { directionsSearchUrl } from '@/core/lib/directions';
import { googleCalendarUrl } from '@/core/lib/calendar';
import { useSession } from '@/core/session';
import { Card, Help, Note } from '@/ui/kit';
import { CalendarIcon, CancelledIcon, PinIcon, PriceIcon } from '@/ui/icons';
import { EventDetails } from './EventDetails';
import { JoinPitch } from './JoinPitch';

/** همان سه آیکونِ صفحه‌ی جزئیاتِ کشف، برای همان سه برچسب. */
const FACT_ICON = { clock: CalendarIcon, pin: PinIcon, tag: PriceIcon };

export function GuestSignal({ source, pageUrl, answering, error, onAnswer }: {
  source: GuestSignalSource;
  /**
   * نشانیِ خودِ همین صفحه — ته توضیحاتِ رخدادِ تقویم می‌نشیند.
   *
   * خودِ لینکِ «به تقویم اضافه کن» از همین‌جا ساخته می‌شود و نه در صفحه‌ها:
   * چهار صفحه (دو لینک × دو اپ) داشتند یک چیز را چهار بار می‌ساختند، و
   * سه‌تاشان `place_name`ِ خالیِ سیگنالِ زاده‌ی رخداد را به تقویم می‌دادند.
   * این‌جا همان «کجا»یی می‌رود که روی صفحه هم نوشته شده.
   */
  pageUrl?: string | null;
  answering?: boolean;
  error?: string | null;
  /**
   * نبودنش یعنی این لینک اصلاً جواب نمی‌گیرد — دکمه‌ها هم ساخته نمی‌شوند.
   * اگر Promise برگرداند، صفحه‌ی «ثبت شد» فقط وقتی می‌آید که واقعاً نشسته
   * باشد؛ خطا یعنی همان صفحه با پیامِ خطا می‌ماند.
   */
  onAnswer?: (r: GuestResponse) => void | Promise<unknown>;
}) {
  useLocale();
  const { session } = useSession();
  /** جوابی که همین حالا داده شد — تا صفحه‌ی «ثبت شد» را نشان دهیم. */
  const [done, setDone] = useState<GuestResponse | null>(null);

  const v = guestView(source);
  const meta = kindMeta(v.kind);
  const answer = v.answer && onAnswer ? v.answer : null;
  const answered = answer ? answer.current !== 'pending' : false;

  /*
   * تقویم فقط وقتی معنی دارد که سیگنال زمانِ مشخصی داشته باشد؛
   * `googleCalendarUrl` خودش برای سیگنالِ بی‌تاریخ `null` می‌دهد و صفحه
   * دکمه‌ای نشان نمی‌دهد که تقویمِ خالی باز کند.
   */
  const calendarUrl = pageUrl ? googleCalendarUrl({
    title: v.title,
    startsAt: source.starts_at,
    endsAt: source.ends_at,
    dateOnly: source.date_only,
    placeName: v.facts.find((f) => f.name === 'where')?.value ?? null,
    note: v.note,
    url: pageUrl,
  }) : null;

  const send = async (r: GuestResponse) => {
    try {
      await onAnswer?.(r);
      setDone(r);
    } catch {
      // والد گزارشش می‌کند و `error` را می‌دهد؛ این‌جا فقط نباید بگوییم «ثبت شد».
    }
  };

  const answerBtn = (r: GuestResponse, label: string) => (
    <button type="button"
      className={`btn${answer?.current === r ? ' fill' : ''}`}
      disabled={answering || answer?.locked}
      onClick={() => send(r)}>
      {label}
    </button>
  );

  if (done) {
    return (
      <div className="feed guest">
        <Card className="guest-done">
          <h1 className="feed-title">
            {done === 'in' ? t('inviteThanksIn')
              : done === 'maybe' ? t('inviteThanksMaybe') : t('inviteSaved')}
          </h1>
          <p className="dim">{v.title} · {v.when}</p>
          {/* دکمه خودش می‌گوید هنوز می‌شود عوضش کرد؛ یک جمله‌ی جدا هم برای
              گفتنِ همان چیز، دو بار گفتنِ یک حرف بود. */}
          <button type="button" className="btn block" onClick={() => setDone(null)}>
            {t('inviteChangeAnswer')}
          </button>
          {/* «به تقویمم اضافه کن» جایش این‌جاست و نه صفحه‌ی قبل: قبل از
              جواب دادن، کسی رخدادی را که شاید نرود توی تقویمش نمی‌گذارد.
              بعد از «هستم» دقیقاً همان کاری‌ست که می‌خواهد بکند. */}
          {calendarUrl && !v.cancelled && done !== 'out' ? (
            <p><a href={calendarUrl} target="_blank" rel="noreferrer">{t('addToCalendar')}</a></p>
          ) : null}
        </Card>

        {/* «حساب بساز» فقط برای کسی که حساب ندارد؛ ولی پیشنهادِ گرفتنِ اپ
            برای هر دو. وارد بودن در مرورگر هیچ نمی‌گوید درباره‌ی اینکه اپ
            روی گوشی هست یا نه — و همین‌جا یک بار اشتباه شد و کارت کلاً برای
            کاربرِ واردشده ناپدید بود. */}
        {done === 'out' ? <JoinPitch variant="link" />
          : <JoinPitch variant={session ? 'app' : 'after'} />}

        <p className="dim guest-home"><Link to="/">{t('backToSite')}</Link></p>
      </div>
    );
  }

  return (
    <div className="feed guest">
      {/* «فلانی می‌پرسه — پایه‌ای؟» درشت و بالای همه‌چیز.
          قبلاً یک خطِ ریزِ کم‌رنگ بود («Parviz is asking:») و کارِ صفحه را
          نمی‌گفت. حالا خودِ سوال است، با همان لحنی که اسمِ محصول دارد: کسی
          که با پیامک می‌آید در همان نگاهِ اول می‌فهمد این صفحه از او چه
          می‌خواهد. */}
      <h2 className="guest-asks-up">{t('guestAsksUp', { name: v.creatorName })}</h2>

      <h1 className="feed-title">
        {v.cancelled ? <CancelledIcon size={18} className="cancel-mark" /> : null}
        {v.title}
      </h1>

      {/* لغو بالای همه‌چیز: جواب دادن و بقیه‌ی صفحه با «این دیگه سرِ
          جاش نیست» بی‌معنی می‌شن. */}
      {v.cancelled ? (
        <Card><Note tone="brand">
          {t('signalCancelledBadge')} — {t('signalCancelledNote')}
        </Note></Card>
      ) : null}

      <Card>
        {/* نوعِ سیگنال با زیرنویسش: «فقط اگه کسی بیاد» برای کسی که اپ را
            نمی‌شناسد یک عبارتِ مبهم است، و دقیقاً همان چیزی‌ست که معنیِ
            جوابِ او را عوض می‌کند. */}
        <h3>{meta.emoji} {t(meta.titleKey)}</h3>

        {/* عکس و صفحه‌ی رسمیِ رخداد؛ مکانش پایین توی ردیفِ برچسب‌دار
            می‌آید، پس این‌جا دوباره نوشته نمی‌شود. */}
        <EventDetails event={v.event} hidePlace />

        {/* کِی / کجا / چند — برچسب‌دار و هم‌اندازه‌ی متنِ اصلی. قبلاً هر
            سه پاراگرافِ کم‌رنگ بودند و «چند» اصلاً معلوم نبود قیمت است. */}
        <div className="factlist guest-facts">
          {v.facts.map((f) => {
            const Glyph = FACT_ICON[f.icon];
            return (
              <div className="fact" key={f.name}>
                <Glyph size={17} />
                <div className="fact-body">
                  <b className="field-label">{f.label}</b>
                  <span>{f.value}</span>
                  {/* «مسیر» فقط کنارِ مکان. جستجوی متنی و نه مختصات، چون
                      این دریچه lat/lng نمی‌دهد و مکان گاهی اصلاً نشانی
                      نیست («خونه‌ی آنی») — نگاه کن به core/lib/directions. */}
                  {f.name === 'where' ? (
                    <a className="btn fact-action" target="_blank" rel="noreferrer"
                      href={directionsSearchUrl(f.value)}>
                      <PinIcon size={15} />
                      {t('directions')}
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {/* حرفِ خودِ میزبان، با نقلِ قول جدا از بقیه: این تنها جمله‌ی
            این صفحه است که یک آدم نوشته، نه یک فیلد. */}
        {v.note ? <p className="guest-note">{v.note}</p> : null}

        {/* لینکِ عمومی صفحه‌ی بعدی ندارد، پس تقویمش همین‌جاست. صفحه‌ای که
            جواب می‌گیرد، تقویم را به بعد از جواب می‌برد. */}
        {calendarUrl && !v.cancelled && !answer ? (
          <p><a href={calendarUrl} target="_blank" rel="noreferrer">{t('addToCalendar')}</a></p>
        ) : null}
      </Card>

      {answer ? (
        <Card>
          {/* «هر کی این لینک را دارد می‌تواند جای تو جواب بدهد» پشتِ «!». */}
          <h3>{t('yourAnswer')}<Help tone="warn" text={t('invitePersonal')} /></h3>
          {error ? <p className="error">{error}</p> : null}

          {/* سه دکمه همیشه دیده می‌شن، حتی بعد از جواب دادن — چون جواب
              تا شروعِ رخداد قابلِ تغییره و مخفی کردنشون یعنی طرف فکر
              کنه دیگه نمی‌شه. */}
          <div className="three-up">
            {answerBtn('in', t('guestIn'))}
            {answerBtn('maybe', t('inviteMaybe'))}
            {answerBtn('out', t('guestOut'))}
          </div>

          {v.cancelled ? null
            : answer.locked ? <Note>{t('inviteLocked')}</Note>
              : answered ? <Note tone="brand">{t('inviteSaved')} {t('inviteChange')}</Note>
                : null}
        </Card>
      ) : null}

      {/* چرا لینکِ عمومی دکمه‌ی جواب نداره و به‌جاش چه کار کنه. بدونِ این،
          صفحه به نظر ناقص می‌آد نه عمدی. */}
      {!answer ? <Card><Note>{t('publicPeek')} {t('publicNoReply')}</Note></Card> : null}

      {/* لینکِ عمومی صفحه‌ی بعدی ندارد، پس تنها فرصتِ پیشنهادِ پیوستن
          همین‌جاست. لینکِ شخصی دارد — آن‌جا فقط یک لینکِ ریز می‌ماند تا
          جلوی کارِ اصلیِ صفحه را نگیرد. */}
      {answer ? <JoinPitch variant="link" />
        : <JoinPitch variant={session ? 'app' : 'full'} />}

      {/* راهی به خودِ سایت. «برگرد» نبود چون جایی برای برگشتن نیست — طرف از
          یک پیامک آمده، نه از صفحه‌ی قبلیِ ما. */}
      <p className="dim guest-home"><Link to="/">{t('backToSite')}</Link></p>
    </div>
  );
}
